import depsloader from "../../../depsloader";

const SEARCH_URL = "https://api.screenwork.de/geocoder/search";
const REVERSE_URL = "https://api.screenwork.de/geocoder/reverse";
const REQUEST_TIMEOUT = 20000;

function mapsKey() {
  const ds = document.documentElement ? document.documentElement.dataset : null;
  return ds && ds.mapsKey ? String(ds.mapsKey) : "";
}

function isFiniteNumber(v) {
  return typeof v === "number" && isFinite(v);
}

function validLatLng(lat, lng) {
  return isFiniteNumber(lat) && isFiniteNumber(lng) &&
    lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
}

function str(v) {
  if (typeof v === "string") {
    const t = v.trim();
    return t ? t : null;
  }
  if (typeof v === "number" && isFinite(v)) {
    return String(v);
  }
  return null;
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.autocompleteSource = null;
    this.reverseSource = null;
  }

  cancelAutocomplete() {
    if (this.autocompleteSource != null) {
      this.autocompleteSource.cancel();
      this.autocompleteSource = null;
    }
  }

  cancelReverse() {
    if (this.reverseSource != null) {
      this.reverseSource.cancel();
      this.reverseSource = null;
    }
  }

  _geoaddressText(g, name) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.zip && g.district) {
      s = g.zip + " " + g.district;
    } else if (g.district) {
      s = g.district;
    } else if (g.zip) {
      s = g.zip;
    }

    const p = [];

    if (!g.street && !g.city && name) {
      p.push(name);
    }

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state && !g.street && g.state !== name) {
      p.push(g.state);
    }

    if (g.country && !g.street && g.country !== name) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  // Liest ein Feature, ohne es zu verändern. Liefert null bei fehlenden
  // oder ungültigen Koordinaten.
  _geoaddress(feature) {
    if (!feature || typeof feature !== "object") {
      return null;
    }

    const geometry = feature.geometry || {};
    const coords = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    // GeoJSON: [lng, lat]
    const lng = coords[0];
    const lat = coords[1];
    if (!validLatLng(lat, lng)) {
      return null;
    }

    const p = feature.properties && typeof feature.properties === "object" ? feature.properties : {};
    const type = str(p.type);
    const name = str(p.name);
    const zip = str(p.postcode);
    const city = str(p.city);
    const district = str(p.district);
    const geoaddress = {};

    if (type) {
      switch (type) {
        case "house":
          geoaddress.street = str(p.street);
          geoaddress.number = str(p.housenumber);
          geoaddress.zip = zip;
          geoaddress.city = city;
          geoaddress.district = district;
          break;
        case "street":
          geoaddress.street = str(p.street) || name;
          geoaddress.zip = zip;
          geoaddress.city = city;
          geoaddress.district = district;
          break;
        case "city":
          geoaddress.zip = zip;
          geoaddress.city = name || city;
          geoaddress.district = district;
          break;
        case "district":
          geoaddress.zip = zip;
          geoaddress.city = city;
          geoaddress.district = district || name;
          break;
        default:
          // locality, county, state, country, other
          geoaddress.street = str(p.street);
          geoaddress.number = geoaddress.street ? str(p.housenumber) : null;
          geoaddress.zip = zip;
          geoaddress.city = city;
          geoaddress.district = district;
          break;
      }
    } else {
      // Legacy-Fallback über osm_key / osm_value
      const osmKey = str(p.osm_key);
      const osmValue = str(p.osm_value);
      let street = str(p.street);
      let featureName = name;

      if (osmKey === "highway") {
        street = name;
        featureName = null;
      }

      if (street) {
        geoaddress.street = street;
        geoaddress.number = str(p.housenumber);
      }

      geoaddress.zip = zip;
      if (city) {
        geoaddress.city = city;
        if (featureName && featureName !== city &&
          (osmKey === "boundary" || (osmKey === "place" && osmValue === "suburb"))) {
          geoaddress.district = featureName;
        } else if (district) {
          geoaddress.district = district;
        }
      } else if (featureName && osmKey === "place" &&
        (osmValue === "city" || osmValue === "town" || osmValue === "village")) {
        geoaddress.city = featureName;
      } else if (district) {
        geoaddress.district = district;
      }
    }

    if (geoaddress.district && geoaddress.district === geoaddress.city) {
      geoaddress.district = null;
    }

    Object.keys(geoaddress).forEach(k => {
      if (geoaddress[k] == null) {
        delete geoaddress[k];
      }
    });

    geoaddress.lat = lat;
    geoaddress.lng = lng;

    const state = str(p.state);
    const country = str(p.country);
    if (state) {
      geoaddress.state = state;
    }
    if (country) {
      geoaddress.country = country;
    }

    geoaddress.text = this._geoaddressText(geoaddress, name);
    if (!geoaddress.text) {
      return null;
    }

    return geoaddress;
  }

  _features(res) {
    return res && res.data && Array.isArray(res.data.features) ? res.data.features : [];
  }

  // Liefert immer ein Promise, das erfüllt oder abgelehnt wird; ein Abbruch
  // durch eine neuere Suche lehnt mit axios.isCancel(err) === true ab.
  autocomplete(q) {
    this.cancelAutocomplete();

    const query = typeof q === "string" ? q.trim() : "";
    const key = mapsKey();
    if (!query || !key) {
      return Promise.resolve([]);
    }

    const source = this.axios.CancelToken.source();
    this.autocompleteSource = source;

    const params = new URLSearchParams();
    params.append("q", query);
    params.append("lang", "de");
    params.append("limit", "10");
    params.append("key", key);

    const done = () => {
      if (this.autocompleteSource === source) {
        this.autocompleteSource = null;
      }
    };

    return this.axios.get(SEARCH_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      done();
      const results = [];
      this._features(res).forEach(f => {
        const geoaddress = this._geoaddress(f);
        if (geoaddress) {
          results.push(geoaddress);
        }
      });
      return results;
    }, err => {
      done();
      throw err;
    });
  }

  reverse(lat, lng) {
    this.cancelReverse();

    const nLat = Number(lat);
    const nLng = Number(lng);
    if (!validLatLng(nLat, nLng)) {
      return Promise.reject(new Error("invalid coordinates"));
    }

    const key = mapsKey();
    if (!key) {
      return Promise.reject(new Error("maps key missing"));
    }

    const source = this.axios.CancelToken.source();
    this.reverseSource = source;

    const params = new URLSearchParams();
    params.append("lat", String(nLat));
    params.append("lon", String(nLng));
    params.append("lang", "de");
    params.append("key", key);

    const done = () => {
      if (this.reverseSource === source) {
        this.reverseSource = null;
      }
    };

    return this.axios.get(REVERSE_URL, {
      cancelToken: source.token,
      timeout: REQUEST_TIMEOUT,
      params
    }).then(res => {
      done();
      const features = this._features(res);
      for (let i = 0; i < features.length; i++) {
        const geoaddress = this._geoaddress(features[i]);
        if (geoaddress) {
          return geoaddress;
        }
      }
      throw new Error("invalid data");
    }, err => {
      done();
      throw err;
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
